import { useQueries, useQuery } from '@tanstack/react-query';

import { reportingApi, reportingKeys, type AggregateInput } from '@/features/reporting/api';
import type { AggregateRow, WidgetConfig } from '@/features/reporting/types';
import type { ApiError } from '@/lib/errors';

export function useReportDatasets() {
  return useQuery({
    queryKey: reportingKeys.datasets,
    queryFn: () => reportingApi.datasets(),
  });
}

/** Runs the aggregation for one widget. Keyed on the full config so it refetches
 *  whenever any dimension/measure/aggregate changes. */
export function useWidgetData(widget: WidgetConfig) {
  const input: AggregateInput = {
    dataset_id: widget.datasetId,
    dimension: widget.dimension,
    measure: widget.measure,
    aggregate: widget.aggregate,
    limit: widget.limit,
    chart_type: widget.chartType,
  };
  return useQuery({
    queryKey: reportingKeys.aggregate(input),
    queryFn: () => reportingApi.aggregate(input),
    enabled: widget.datasetId !== '',
  });
}

export interface CompareResult {
  rows: AggregateRow[];
  isPending: boolean;
  isError: boolean;
  error: ApiError | null;
}

/** Compare mode: aggregate the same measure across several datasets (one
 *  un-grouped query per table) and return one labelled row per table. */
export function useCompareData(widget: WidgetConfig): CompareResult {
  const ids = widget.datasetIds ?? [];
  const names = widget.datasetNames ?? [];

  const results = useQueries({
    queries: ids.map((id) => {
      const input: AggregateInput = {
        dataset_id: id,
        dimension: null,
        measure: widget.measure,
        aggregate: widget.aggregate,
        limit: 1,
      };
      return {
        queryKey: reportingKeys.aggregate(input),
        queryFn: () => reportingApi.aggregate(input),
        enabled: id !== '',
      };
    }),
  });

  const rows: AggregateRow[] = results.map((r, i) => ({
    label: names[i] ?? ids[i] ?? '',
    value: r.data?.[0]?.value ?? 0,
  }));

  return {
    rows,
    isPending: results.some((r) => r.isPending),
    isError: results.some((r) => r.isError),
    error: (results.find((r) => r.isError)?.error as ApiError | undefined) ?? null,
  };
}
